// src/hooks/useAuth.ts
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import type { UseAuthReturn, User } from '../types';

export const useAuth = (): UseAuthReturn => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  // Helper function to get user from localStorage (for compatibility with ProtectedRoute)
  const getUserFromStorage = (): User | null => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        return JSON.parse(userData) as User;
      } catch (error) {
        console.error('Error parsing user data:', error);
        return null;
      }
    }
    return null;
  };
  
  // Helper function to check authentication status
  const checkAuth = (): boolean => {
    const token = localStorage.getItem('token');
    const user = getUserFromStorage();
    return !!token && !!user;
  };
  
  // Get loading state (compatibility with ProtectedRoute)
  const loading = context.isLoading;
  
  // Get authentication status
  const isAuthenticated = checkAuth();
  
  // Get user from context (preferred) or localStorage
  const user = context.user || getUserFromStorage();
  
  // Get user role
  const getUserRole = (): 'rider' | 'customer' | 'admin' | 'support' | undefined => {
    return user?.role;
  };
  
  // Check if user has specific role
  const hasRole = (role: 'rider' | 'customer'): boolean => {
    return user?.role === role;
  };
  
  // Check if user has any role
  const hasAnyRole = (): boolean => {
    const role = user?.role;
    return role === 'rider' || role === 'customer' || role === 'admin' || role === 'support';
  };
  
  // Redirect to appropriate dashboard
  const redirectToDashboard = (navigate: any): void => {
    const role = getUserRole();
    if (role === 'rider') {
      navigate('/dashboard/rider');
    } else if (role === 'customer') {
      navigate('/dashboard/customer');
    } else if (role === 'admin' || role === 'support') {
      navigate('/admin');
    } else {
      navigate('/choose-role');
    }
  };
  
  // Check if user can access route based on roles
  const canAccessRoute = (allowedRoles: string[]): boolean => {
    const userRole = getUserRole();
    if (!userRole) return false;
    
    if (allowedRoles.length === 0) return true; // Empty array means all authenticated users can access
    
    return allowedRoles.includes(userRole);
  };
  
  return {
    ...context,
    // New methods for ProtectedRoute compatibility
    getUserFromStorage,
    checkAuth,
    loading,
    isAuthenticated,
    user,
    getUserRole,
    hasRole,
    hasAnyRole,
    redirectToDashboard,
    canAccessRoute
  };
};

// Also export as default for backward compatibility
export default useAuth;